import {
  BREAK_CATEGORIES,
  type MarketPrice,
  type NewSealedProduct,
  SEALED_FORMATS,
  type SealedFormat,
  type SealedProduct,
  type SealedStockUpdate,
} from "@hobbyshopify/backend-client";
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { InventoryTabs } from "../../components/InventoryTabs";
import { LoadFailed, Loading } from "../../components/LoadState";
import { MarketEstimatePanel } from "../../components/MarketEstimatePanel";
import { useModalLayer } from "../../components/ModalLayer";
import { PanelHead } from "../../components/PanelHead";
import { useFocusFallback } from "../../components/use-focus-fallback";
import { type MarketRefreshStatus, marketFreshness } from "../../core/primitives/catalog-market";
import { formatWhen } from "../../core/primitives/format";
import { dollarsToCents, formatCents } from "../../core/primitives/live";
import type { LoadStatus } from "../../core/primitives/load-status";
import { rowStandIn } from "./sealed-focus";
import { SEALED_FILTERS, SealedActions, SealedState } from "./sealed-model";

const FORMAT_LABELS: Record<SealedFormat, string> = {
  HOBBY_BOX: "Hobby box",
  BLASTER_BOX: "Blaster box",
  HANGER_BOX: "Hanger box",
  MEGA_BOX: "Mega box",
  CASE: "Case",
  TIN: "Tin",
  BOOSTER_BOX: "Booster box",
  OTHER: "Other",
};

export function sealedSubtitle(p: SealedProduct): string {
  return [p.brand, p.year ? String(p.year) : null, p.setName, FORMAT_LABELS[p.format]].filter(Boolean).join(" · ");
}

/**
 * A dialog over the page, as the app's others: the page behind it inert, Escape and a press outside close it unless
 * it is saving, and it scrolls within the screen however tall its form.
 */
function SealedDialog({
  title,
  subtitle,
  wide,
  busy,
  initialFocus,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  wide?: boolean;
  busy: boolean;
  initialFocus: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();
  const close = () => {
    if (!busy) onClose();
  };
  useModalLayer(initialFocus, close);
  return createPortal(
    <div className="scrim items-end justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={close}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`modal relative max-h-full w-full overflow-y-auto overscroll-contain p-6 ${wide ? "max-w-md" : "max-w-sm"}`}
      >
        <h2 id={titleId} className={`${subtitle ? "mb-1" : "mb-4"} text-base font-semibold text-white`}>
          {title}
        </h2>
        {subtitle && <p className="mb-4 truncate text-sm text-muted">{subtitle}</p>}
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function SealedPage() {
  const state = useSubtree(SealedState);
  const actions = useSubtree(SealedActions);
  const observer = useObserver();

  const filter = observer.watch(state.filter);
  const search = observer.watch(state.search);
  const status = observer.watch(state.status);
  const answered = observer.watch(state.answered);
  const items = observer.watch(state.items);
  const held = observer.watch(state.held);
  const dialogChecking = observer.watch(state.dialogChecking);
  const dialog = observer.watch(state.dialog);
  const dialogBusy = observer.watch(state.dialogBusy);
  const dialogError = observer.watch(state.dialogError);
  const market = observer.watch(state.market);
  const marketStatus = observer.watch(state.marketStatus);
  const marketRefresh = observer.watch(state.marketRefresh);
  const keepRef = useRef<HTMLButtonElement>(null);
  const reviewCount = observer.watch(state.reviewCount);
  const areaRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The products as the list last drew them: the layout effect below runs before this one in the same commit.
  const drawn = useRef<string[]>([]);
  useEffect(() => {
    drawn.current = items.map((p) => p.id);
  });
  // A row's buttons go when the list reads its product archived: the focus stays on the row, now saying so, or goes
  // to the row standing where it stood once it has left the list; the heading takes it when no row is left.
  useFocusFallback(areaRef, (left) => {
    const rows = Array.from(listRef.current?.children ?? []).filter((e): e is HTMLElement => e instanceof HTMLElement);
    const stand = rowStandIn(left, rows, drawn.current) ?? headingRef.current;
    if (stand === headingRef.current && stand && !stand.hasAttribute("tabindex")) stand.setAttribute("tabindex", "-1");
    return stand;
  });

  const loading = status.kind === "idle" || status.kind === "loading";

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="panel p-6">
        <PanelHead
          className="mb-3"
          action={
            <button type="button" className="btn-primary btn-sm sm:ml-auto" onClick={actions.openAdd}>
              Add product
            </button>
          }
        >
          <h1 ref={headingRef} className="text-lg font-bold text-white">
            Inventory
          </h1>
          <InventoryTabs active="sealed" reviewCount={reviewCount} />
        </PanelHead>
        <p className="mb-6 text-sm text-muted">
          Sealed product: boxes, cases and tins. What a live break opens comes from here.
        </p>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {SEALED_FILTERS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                onClick={() => actions.setFilter(tab.key)}
                className={`rounded-btn px-3 py-1.5 text-xs font-medium transition-colors ${
                  filter === tab.key
                    ? "bg-accent text-ink"
                    : "bg-surface text-muted hover:bg-surface-strong hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            placeholder="Search name, brand, set…"
            className="field max-w-xs"
            value={search}
            onChange={(e) => actions.setSearch(e.target.value)}
          />
        </div>

        <div ref={areaRef}>
          {status.kind === "error" && (
            <LoadFailed title="Couldn't load your sealed product." message={status.message} onRetry={actions.retry} />
          )}

          {/* Read again, the empty list stays with its Add: the focus the first box's dialog gives back stays on it. */}
          {loading && !answered ? (
            <Loading label="Loading your sealed product…" />
          ) : status.kind === "error" && items.length === 0 ? null : items.length === 0 ? (
            <div className="py-10 text-center">
              <p className="mb-4 text-sm text-muted">
                {search || filter !== "all" ? "Nothing matches that." : "No sealed product yet."}
              </p>
              {!search && filter === "all" && (
                <button type="button" className="btn-primary btn-sm" onClick={actions.openAdd}>
                  Add your first box
                </button>
              )}
            </div>
          ) : (
            <div ref={listRef} className="space-y-2">
              {items.map((p) => (
                <SealedRow
                  key={p.id}
                  product={p}
                  busy={held.has(p.id)}
                  onSetStock={() => actions.openStock(p)}
                  onArchive={() => actions.openArchive(p)}
                  onMarket={() => void actions.openMarket(p)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {dialog?.kind === "add" && (
        <AddSealedModal
          busy={dialogBusy}
          checking={dialogChecking}
          error={dialogError}
          onSave={(p) => void actions.saveNew(p)}
          onCancel={actions.closeDialog}
        />
      )}
      {dialog?.kind === "stock" && (
        <SealedStockModal
          product={dialog.product}
          busy={dialogBusy}
          checking={dialogChecking}
          error={dialogError}
          onSave={(c) => void actions.saveStock(c)}
          onCancel={actions.closeDialog}
        />
      )}
      {dialog?.kind === "market" && (
        <SealedMarketModal
          product={dialog.product}
          market={market}
          status={marketStatus}
          refresh={marketRefresh}
          onRefresh={() => void actions.refreshMarket()}
          onClose={actions.closeDialog}
        />
      )}
      {dialog?.kind === "archive" && (
        <SealedDialog
          title="Archive this product?"
          subtitle={dialog.product.name}
          busy={dialogBusy}
          initialFocus={keepRef}
          onClose={actions.closeDialog}
        >
          <p className="text-sm text-muted">
            It leaves the list and the show builder. Nothing already sold or broken changes.
          </p>
          {dialogError && (
            <p role="alert" className="note note-danger mt-3 text-danger">
              {dialogError}
            </p>
          )}
          {/* Held, not disabled, while it goes: the focus stays on the answer pressed. */}
          <div className="mt-6 flex gap-3">
            <button
              ref={keepRef}
              type="button"
              onClick={dialogBusy ? undefined : actions.closeDialog}
              aria-disabled={dialogBusy || undefined}
              className="btn-secondary flex-1 aria-disabled:opacity-60"
            >
              Keep
            </button>
            <button
              type="button"
              onClick={dialogBusy || dialogChecking ? undefined : () => void actions.confirmArchive()}
              aria-disabled={dialogBusy || dialogChecking || undefined}
              className="btn-primary flex-1 aria-disabled:opacity-60"
            >
              {dialogBusy ? "Archiving…" : "Archive"}
            </button>
          </div>
        </SealedDialog>
      )}
    </div>
  );
}

export function SealedRow({
  product,
  busy,
  onSetStock,
  onArchive,
  onMarket,
}: {
  product: SealedProduct;
  busy: boolean;
  onSetStock: () => void;
  onArchive: () => void;
  onMarket?: () => void;
}) {
  const freshness = marketFreshness(product.marketEstimate);
  const subtitle = sealedSubtitle(product);
  // The buttons say whose they are: the focus can come to them from another row.
  const nameId = `sealed-name-${product.id}`;
  return (
    <div data-product={product.id} className="flex gap-3 rounded-row border border-line bg-surface p-4">
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-faint">Box</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div id={nameId} className="truncate font-medium text-white">
          {product.name}
        </div>
        {subtitle && <div className="truncate text-xs text-muted">{subtitle}</div>}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {product.status === "ARCHIVED" ? (
            <span className="badge badge-neutral">Archived</span>
          ) : product.available > 0 ? (
            <span className="badge badge-neutral">{product.available} available</span>
          ) : (
            <span className="badge badge-warn">Out of stock</span>
          )}
          {product.reserved > 0 && <span className="text-muted">{product.reserved} held by breaks</span>}
          {product.costCents != null && <span className="text-muted">cost {formatCents(product.costCents)}</span>}
          {product.marketValueCents != null && (
            <span className="text-muted">asking {formatCents(product.marketValueCents)}</span>
          )}
          {product.marketEstimate?.marketCents != null && (
            <span className={freshness === "stale" ? "text-warn" : "text-muted"}>
              market {formatCents(product.marketEstimate.marketCents)}
              {freshness === "stale" && " (out of date)"}
            </span>
          )}
        </div>
      </div>
      {product.status === "ACTIVE" && (
        <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
          {onMarket && (
            <button
              type="button"
              className="btn-ghost btn-xs"
              data-product={product.id}
              aria-describedby={nameId}
              onClick={onMarket}
            >
              Market
            </button>
          )}
          {/* Held, not disabled, while the list is read again after its save: the focus comes back here. */}
          <button
            type="button"
            className="btn-secondary btn-xs aria-disabled:opacity-60"
            data-product={product.id}
            aria-describedby={nameId}
            aria-disabled={busy}
            onClick={busy ? undefined : onSetStock}
          >
            Set stock
          </button>
          <button
            type="button"
            className="btn-ghost btn-xs aria-disabled:opacity-60"
            data-product={product.id}
            aria-describedby={nameId}
            aria-disabled={busy}
            onClick={busy ? undefined : onArchive}
          >
            Archive
          </button>
        </div>
      )}
    </div>
  );
}

function AddSealedModal({
  busy,
  checking,
  error,
  onSave,
  onCancel,
}: {
  busy: boolean;
  /** After a save that threw: Save waits for the list's answer, Cancel does not. */
  checking: boolean;
  error: string | null;
  onSave: (product: NewSealedProduct) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [year, setYear] = useState("");
  const [set, setSet] = useState("");
  const [category, setCategory] = useState<SealedProduct["category"]>("CUSTOM");
  const [format, setFormat] = useState<SealedFormat>("HOBBY_BOX");
  const [quantity, setQuantity] = useState("1");
  const [cost, setCost] = useState("");
  const [marketValue, setMarketValue] = useState("");

  const nameRef = useRef<HTMLInputElement>(null);
  const qty = Number(quantity);
  const valid = name.trim() !== "" && Number.isInteger(qty) && qty >= 0;

  const submit = () => {
    const parsedYear = Number(year);
    onSave({
      name: name.trim(),
      brand: brand.trim() || undefined,
      year: Number.isInteger(parsedYear) && parsedYear > 0 ? parsedYear : undefined,
      setName: set.trim() || undefined,
      category,
      format,
      quantity: qty,
      costCents: cost.trim() ? dollarsToCents(cost) : undefined,
      marketValueCents: marketValue.trim() ? dollarsToCents(marketValue) : undefined,
    });
  };

  return (
    <SealedDialog title="Add sealed product" wide busy={busy} initialFocus={nameRef} onClose={onCancel}>
      <div className="space-y-3">
        <label className="block">
          <span className="label">Name</span>
          <input
            ref={nameRef}
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="2025 Prizm Basketball Hobby Box"
            maxLength={200}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">Brand</span>
            <input className="field" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Panini" />
          </label>
          <label className="block">
            <span className="label">Year</span>
            <input
              className="field"
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="2025"
            />
          </label>
        </div>
        <label className="block">
          <span className="label">Set</span>
          <input className="field" value={set} onChange={(e) => setSet(e.target.value)} placeholder="Prizm" />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">Sport or game</span>
            <select
              className="field"
              value={category}
              onChange={(e) => setCategory(e.target.value as SealedProduct["category"])}
            >
              {BREAK_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c === "CUSTOM" ? "Other" : c}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Format</span>
            <select className="field" value={format} onChange={(e) => setFormat(e.target.value as SealedFormat)}>
              {SEALED_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {FORMAT_LABELS[f]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="label">On hand</span>
            <input
              className="field"
              type="number"
              min={0}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">Cost each</span>
            <input
              className="field"
              inputMode="decimal"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="$450"
            />
          </label>
          <label className="block">
            <span className="label">Worth each</span>
            <input
              className="field"
              inputMode="decimal"
              value={marketValue}
              onChange={(e) => setMarketValue(e.target.value)}
              placeholder="$600"
            />
          </label>
        </div>
        <p className="text-xs text-faint">Both private. The show builder uses them as its starting numbers.</p>
      </div>

      {error && (
        <p role="alert" className="note note-danger mt-3 text-danger">
          {error}
        </p>
      )}

      {/* Held, not disabled, while it saves: the focus stays where it was. */}
      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={busy ? undefined : onCancel}
          aria-disabled={busy || undefined}
          className="btn-secondary flex-1 aria-disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={busy || checking ? undefined : submit}
          disabled={!busy && !checking && !valid}
          aria-disabled={busy || checking || undefined}
          className="btn-primary flex-1 aria-disabled:opacity-60"
        >
          {busy ? "Saving…" : "Add"}
        </button>
      </div>
    </SealedDialog>
  );
}

function SealedStockModal({
  product,
  busy,
  checking,
  error,
  onSave,
  onCancel,
}: {
  product: SealedProduct;
  busy: boolean;
  /** After a save that threw: Save waits for the list's answer, Cancel does not. */
  checking: boolean;
  error: string | null;
  onSave: (change: SealedStockUpdate) => void;
  onCancel: () => void;
}) {
  const [quantity, setQuantity] = useState(String(product.onHand));
  const [kind, setKind] = useState<"ADJUSTED" | "SHRINKAGE">("ADJUSTED");
  const [reason, setReason] = useState("");
  const [cost, setCost] = useState("");

  const quantityRef = useRef<HTMLInputElement>(null);
  const parsed = Number(quantity);
  const valid = Number.isInteger(parsed) && parsed >= 0;
  const delta = valid ? parsed - product.onHand : 0;

  return (
    <SealedDialog
      title="Set stock level"
      subtitle={product.name}
      busy={busy}
      initialFocus={quantityRef}
      onClose={onCancel}
    >
      <div className="space-y-3">
        <div>
          <div className="mb-1 block text-xs font-medium text-muted">On hand</div>
          <input
            ref={quantityRef}
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            className={`field ${valid ? "" : "border-danger focus:border-danger"}`}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
          <p className="mt-1 text-xs text-faint">
            {valid
              ? delta === 0
                ? `Currently ${product.onHand}. No change.`
                : `Currently ${product.onHand} — this records ${delta > 0 ? `+${delta}` : delta}.`
              : "Enter a whole number, zero or more."}
            {product.reserved > 0 && ` ${product.reserved} held by live breaks.`}
          </p>
        </div>

        {delta < 0 && (
          <div>
            <div className="mb-1 block text-xs font-medium text-muted">Why fewer?</div>
            <select className="field" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="ADJUSTED">We miscounted</option>
              <option value="SHRINKAGE">Lost, stolen or damaged</option>
            </select>
          </div>
        )}

        {delta > 0 && (
          <div>
            <div className="mb-1 block text-xs font-medium text-muted">Cost each (optional)</div>
            <input
              className="field"
              inputMode="decimal"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="$450"
            />
          </div>
        )}

        <div>
          <div className="mb-1 block text-xs font-medium text-muted">Note</div>
          <input
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={delta < 0 ? "Water damage, shelf recount…" : "Recount, restocked…"}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="note note-danger mt-3 text-danger">
          {error}
        </p>
      )}

      {/* Held, not disabled, while it saves: the focus stays where it was. */}
      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={busy ? undefined : onCancel}
          aria-disabled={busy || undefined}
          className="btn-secondary flex-1 aria-disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={
            busy || checking
              ? undefined
              : () =>
                  onSave({
                    quantity: parsed,
                    kind,
                    reason: reason.trim() || undefined,
                    costCents: delta > 0 && cost.trim() ? dollarsToCents(cost) : undefined,
                  })
          }
          disabled={!busy && !checking && (!valid || delta === 0)}
          aria-disabled={busy || checking || undefined}
          className="btn-primary flex-1 aria-disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </SealedDialog>
  );
}

/** The shared market estimate and its history: the same for every shop holding the product. */
function SealedMarketModal({
  product,
  market,
  status,
  refresh,
  onRefresh,
  onClose,
}: {
  product: SealedProduct;
  market: MarketPrice | null;
  status: LoadStatus;
  refresh: MarketRefreshStatus;
  onRefresh: () => void;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  // Newest first reads better in a list, though the API answers oldest first.
  const history = [...(market?.history ?? [])].reverse();
  return (
    <SealedDialog
      title="Market price"
      subtitle={product.name}
      wide
      busy={false}
      initialFocus={closeRef}
      onClose={onClose}
    >
      {status.kind === "loading" || status.kind === "idle" ? (
        <p className="py-4 text-sm text-muted">Loading…</p>
      ) : status.kind === "error" ? (
        <p role="alert" className="py-4 text-sm text-danger">
          Failed to load the market price.
        </p>
      ) : (
        <>
          <MarketEstimatePanel estimate={market?.estimate} refresh={refresh} onRefresh={onRefresh} />
          {history.length > 0 && (
            <ul className="mt-3 max-h-72 divide-y divide-line overflow-y-auto">
              {history.map((point) => (
                <li key={point.retrievedAt.toISOString()} className="flex items-center justify-between gap-3 py-2">
                  <span className="text-xs text-muted">{formatWhen(point.retrievedAt)}</span>
                  <span className="text-sm font-semibold text-muted">
                    {point.marketCents != null ? formatCents(point.marketCents) : "—"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[10px] text-faint">
            Shared across every shop holding this product. Refreshing never changes your asking price.
          </p>
        </>
      )}
      <button ref={closeRef} type="button" onClick={onClose} className="btn-secondary mt-6 w-full">
        Close
      </button>
    </SealedDialog>
  );
}
